import React from 'react';
import {
    Grid2,
    TextField,
    Chip,
    Box,
    Paper,
    Button,
    Drawer,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchInput from '../../../components/atom/SearchInput';
import { BOOKING_STATUSES, BOOKING_STATUS_COLORS } from '../../../helpers/constants';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import EventIcon from '@mui/icons-material/Event';
import { useGetShows } from '@openapi/shows/shows';
import { useGetTimeslots } from '@openapi/timeslots/timeslots';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import { getGetVenueCommissionGroupsQueryOptions } from '@openapi/commission-groups/commission-groups';
import { getGetPaymentMethodsQueryOptions } from '@openapi/payment-methods/payment-methods';
import { useQueries } from '@tanstack/react-query';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { CAPABILITIES } from '../../../helpers/capabilities';
import TimeslotCalendar from '../../../components/TimeslotCalendar';
import { getSlotTemplate } from '../../../helpers/slotTemplate';

const BookingsFilter = ({
    filters,
    setFilters,
    setParams,
    limit,
    total,
    totalRevenue,
    totalPersons,
    revenueBookingCount,
    summaryEstimated,
    loading,
}) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const { can } = useCapabilities();
    const canViewStats = can(CAPABILITIES.BOOKINGS_SHOW_STATS);
    const canViewCommissionGroups = can(CAPABILITIES.COMMISSION_GROUPS_VIEW);
    const canViewPaymentMethods = can(CAPABILITIES.PAYMENT_METHODS_VIEW);

    const { data: showsData } = useGetShows({
        page: 0,
        pageSize: 10000,
        sort: 'title',
        sortDir: 'ASC',
    });

    const shows = showsData?.shows ?? [];
    const showById = new Map(shows.map((s) => [String(s.id), s]));

    const { data: timeslotsData } = useGetTimeslots(
        {
            optionalId: filters.showId || undefined,
            page: 0,
            pageSize: 10000,
            sort: 'starttime',
            sortDir: 'ASC',
            includeBlocks: true,
        },
        {
            query: {
                enabled: !!filters.showId,
            },
        },
    );

    const { data: ticketTypesData } = useGetTicketTypes({
        optionalId: filters.showId || undefined,
        page: 0,
        pageSize: 10000,
    });

    const uniqueVenueIds = [...new Set(shows.flatMap((s) => (s.venueId ? [s.venueId] : [])))];

    // Commission group + payment method options power the advanced-drawer filters
    // only, so fetch them lazily: gated on the matching view capability and on the
    // drawer being open (or a value already being selected, so chips can resolve
    // a label). No per-venue fan-out on mount, and no 401s for booking viewers
    // that lack those capabilities.
    const commissionGroupQueries = useQueries({
        queries: uniqueVenueIds.map((vid) =>
            getGetVenueCommissionGroupsQueryOptions(vid, {
                query: {
                    enabled:
                        canViewCommissionGroups &&
                        (drawerOpen || filters.commissionGroupId != null),
                },
            }),
        ),
    });
    const allCommissionGroups = commissionGroupQueries.flatMap(
        (q) => q.data?.commissionGroups ?? [],
    );

    const paymentMethodQueries = useQueries({
        queries: uniqueVenueIds.map((vid) =>
            getGetPaymentMethodsQueryOptions(
                vid,
                { page: 0, pageSize: 10000 },
                {
                    query: {
                        enabled:
                            canViewPaymentMethods &&
                            (drawerOpen || filters.paymentMethodSlug != null),
                    },
                },
            ),
        ),
    });
    const allPaymentMethods = paymentMethodQueries.flatMap((q) => q.data?.paymentMethods ?? []);

    // venueId of the selected show (null when none selected).
    const selectedShowVenueId = filters.showId
        ? (showById.get(String(filters.showId))?.venueId ?? null)
        : null;

    // Options are scoped to the selected show (commission groups are per-show,
    // payment methods per-venue). With no show selected we show the full list,
    // deduped by slug for payment methods since the filter keys on slug.
    const visibleCommissionGroups = filters.showId
        ? allCommissionGroups.filter((g) => String(g.showId) === String(filters.showId))
        : allCommissionGroups;

    const visiblePaymentMethods = selectedShowVenueId
        ? allPaymentMethods.filter((p) => String(p.venueId) === String(selectedShowVenueId))
        : [...new Map(allPaymentMethods.map((pm) => [pm.slug, pm])).values()];

    const handleFilterChange = (keyOrUpdates, value) => {
        const updates = typeof keyOrUpdates === 'string' ? { [keyOrUpdates]: value } : keyOrUpdates;
        setFilters((prev) => ({ ...prev, ...updates }));
        if (setParams) {
            setParams((prev) => ({ ...prev, page: 0 }));
        }
    };

    const selectStatus = (status) => {
        handleFilterChange('statuses', status ? [status] : []);
    };

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };

    const anyFilterActive = Boolean(
        filters.search ||
        filters.bookingId ||
        filters.confirmationCode ||
        filters.statuses.length > 0 ||
        filters.timeslotId != null ||
        filters.showId != null ||
        filters.ticketTypeId != null ||
        filters.commissionGroupId != null ||
        filters.paymentMethodSlug != null,
    );

    const isAdvancedActive = Boolean(
        filters.bookingId ||
        filters.confirmationCode ||
        filters.statuses.length > 0 ||
        filters.commissionGroupId != null ||
        filters.paymentMethodSlug != null,
    );

    const onClearAll = () => {
        setFilters({
            search: '',
            bookingId: undefined,
            confirmationCode: undefined,
            timeslotId: null,
            showId: null,
            statuses: [],
            ticketTypeId: null,
            commissionGroupId: null,
            paymentMethodSlug: null,
        });
        if (setParams) {
            setParams((prev) => ({ ...prev, page: 0 }));
        }
    };

    // Shared option-label formatters, used by both the dropdowns and the chips.
    const formatTicketType = (tt) =>
        !filters.showId && tt.showTitle ? `${tt.name} (${tt.showTitle})` : tt.name;
    const formatCommissionGroup = (group) => {
        const showTitle = !filters.showId ? showById.get(String(group.showId))?.title : null;
        return showTitle ? `${group.name} (${showTitle})` : group.name;
    };

    // Ticket types are show-scoped server-side; also drop any whose show is not in
    // the accessible show list (defensive).
    const visibleTicketTypes = (ticketTypesData?.ticketTypes ?? []).filter((tt) =>
        showById.has(String(tt.showId)),
    );

    const getShowTitle = (id) => showById.get(String(id))?.title ?? id;

    const getTimeslotLabel = (id) => {
        const timeslot = timeslotsData?.timeslots?.find((t) => String(t.id) === String(id));
        return timeslot ? formatDateTimeInVenueTz(timeslot.startTime) : id;
    };

    const getTicketTypeLabel = (id) => {
        const tt = ticketTypesData?.ticketTypes?.find((t) => String(t.id) === String(id));
        return tt ? formatTicketType(tt) : id;
    };

    const getCommissionGroupLabel = (id) => {
        const group = allCommissionGroups.find((g) => String(g.id) === String(id));
        return group ? formatCommissionGroup(group) : id;
    };

    const getPaymentMethodLabel = (slug) => {
        const pm = allPaymentMethods.find((p) => p.slug === slug);
        return pm ? pm.name : slug;
    };

    return (
        <Grid2 container spacing={2} sx={{ mb: 2, width: '100%' }}>
            <Grid2 size={{ xs: 12 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        width: '100%',
                    }}
                >
                    <Grid2 container spacing={2} alignItems="flex-end">
                        <Grid2 size={{ xs: 12, md: 3 }}>
                            <SearchInput
                                search={filters.search}
                                setSearch={(val) => handleFilterChange('search', val)}
                                setParams={setParams}
                                limit={limit}
                                fullWidth
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="show-filter-label">{t('shows.title')}</InputLabel>
                                <Select
                                    labelId="show-filter-label"
                                    value={filters.showId ?? ''}
                                    label={t('shows.title')}
                                    onChange={(e) => {
                                        const val = e.target.value || null;
                                        // Ticket type, timeslot, commission group and
                                        // payment method are all show-scoped, so reset
                                        // them when the show changes.
                                        handleFilterChange({
                                            timeslotId: null,
                                            showId: val,
                                            ticketTypeId: null,
                                            commissionGroupId: null,
                                            paymentMethodSlug: null,
                                        });
                                    }}
                                    MenuProps={{
                                        autoFocus: false,
                                        disableRestoreFocus: true,
                                        PaperProps: { sx: { maxHeight: 300 } },
                                    }}
                                >
                                    <MenuItem value="">{t('common.all')}</MenuItem>
                                    {showsData?.shows?.map((show) => (
                                        <MenuItem key={show.id} value={show.id}>
                                            {show.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="ticket-type-filter-label">
                                    {t('bookings.filterLabels.ticketType')}
                                </InputLabel>
                                <Select
                                    labelId="ticket-type-filter-label"
                                    value={filters.ticketTypeId ?? ''}
                                    label={t('bookings.filterLabels.ticketType')}
                                    onChange={(e) =>
                                        handleFilterChange('ticketTypeId', e.target.value || null)
                                    }
                                    MenuProps={{
                                        autoFocus: false,
                                        disableRestoreFocus: true,
                                        PaperProps: { sx: { maxHeight: 300 } },
                                    }}
                                >
                                    <MenuItem value="">{t('common.all')}</MenuItem>
                                    {visibleTicketTypes.map((tt) => (
                                        <MenuItem key={tt.id} value={tt.id}>
                                            {formatTicketType(tt)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                        <Grid2
                            size={{ xs: 12, md: 5 }}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'flex-end',
                                alignItems: { xs: 'stretch', md: 'center' },
                                gap: 1,
                            }}
                        >
                            <Button
                                variant={filters.timeslotId != null ? 'contained' : 'outlined'}
                                startIcon={<EventIcon />}
                                disabled={!filters.showId}
                                onClick={() => setPickerOpen(true)}
                                sx={{ height: 40 }}
                            >
                                {filters.timeslotId
                                    ? getTimeslotLabel(filters.timeslotId)
                                    : t('timeslots.title')}
                            </Button>
                            <Button
                                variant={isAdvancedActive ? 'contained' : 'outlined'}
                                startIcon={<FilterListIcon />}
                                onClick={toggleDrawer(true)}
                                sx={{ height: 40 }}
                            >
                                {t('bookings.advancedFiltering')}
                            </Button>
                        </Grid2>

                        {anyFilterActive && (
                            <Grid2
                                size={{ xs: 12 }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    pt: 1,
                                    mt: 1,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                {filters.search && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.search')}: ${filters.search}`}
                                        onDelete={() => handleFilterChange('search', '')}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.showId != null && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.show')}: ${getShowTitle(filters.showId)}`}
                                        onDelete={() =>
                                            handleFilterChange({ showId: null, timeslotId: null })
                                        }
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.timeslotId != null && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.timeslot')}: ${getTimeslotLabel(
                                            filters.timeslotId,
                                        )}`}
                                        onDelete={() => handleFilterChange('timeslotId', null)}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.bookingId && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.id')}: ${filters.bookingId}`}
                                        onDelete={() => handleFilterChange('bookingId', undefined)}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.confirmationCode && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.confirmationCode')}: ${filters.confirmationCode}`}
                                        onDelete={() =>
                                            handleFilterChange('confirmationCode', undefined)
                                        }
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.statuses.length > 0 &&
                                    filters.statuses.map((status) => (
                                        <Chip
                                            key={status}
                                            label={t(`bookings.statusValues.${status}`)}
                                            onDelete={() => selectStatus(null)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                {filters.ticketTypeId != null && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.ticketType')}: ${getTicketTypeLabel(filters.ticketTypeId)}`}
                                        onDelete={() => handleFilterChange('ticketTypeId', null)}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.commissionGroupId != null && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.commissionGroup')}: ${getCommissionGroupLabel(filters.commissionGroupId)}`}
                                        onDelete={() =>
                                            handleFilterChange('commissionGroupId', null)
                                        }
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {filters.paymentMethodSlug != null && (
                                    <Chip
                                        label={`${t('bookings.filterLabels.paymentMethod')}: ${getPaymentMethodLabel(filters.paymentMethodSlug)}`}
                                        onDelete={() =>
                                            handleFilterChange('paymentMethodSlug', null)
                                        }
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={onClearAll}
                                    sx={{ textTransform: 'none', ml: 'auto' }}
                                >
                                    {t('bookings.clearAll')}
                                </Button>
                            </Grid2>
                        )}
                        {canViewStats && (
                            <Grid2
                                size={{ xs: 12 }}
                                sx={{
                                    pt: 2,
                                    mt: 2,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Grid2 container spacing={2}>
                                    {[
                                        {
                                            label: t('bookings.summary.totalBookings'),
                                            value: loading ? '-' : total || 0,
                                        },
                                        {
                                            label: t('bookings.summary.totalRevenue'),
                                            value: loading
                                                ? '-'
                                                : `${formatCentsToUsd(totalRevenue || 0)}${summaryEstimated ? '+' : ''}`,
                                        },
                                        {
                                            label: t('bookings.summary.avgRevenue'),
                                            value: loading
                                                ? '-'
                                                : `${formatCentsToUsd(revenueBookingCount > 0 ? totalRevenue / revenueBookingCount : 0)}${summaryEstimated ? '+' : ''}`,
                                        },
                                        {
                                            label: t('bookings.summary.totalPersons'),
                                            value: loading
                                                ? '-'
                                                : `${totalPersons || 0}${summaryEstimated ? '+' : ''}`,
                                        },
                                    ].map((item, index) => (
                                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    backgroundColor: 'background.default',
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.5,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        </Grid2>
                                    ))}
                                </Grid2>
                            </Grid2>
                        )}
                    </Grid2>
                </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12 }} container spacing={1}>
                <Chip
                    label={t('common.all')}
                    onClick={() => selectStatus(null)}
                    variant={filters.statuses.length === 0 ? 'filled' : 'outlined'}
                    sx={{
                        backgroundColor:
                            filters.statuses.length === 0 ? 'text.secondary' : 'transparent',
                        color: filters.statuses.length === 0 ? 'background.paper' : undefined,
                        borderColor: 'text.secondary',
                    }}
                />
                {Object.values(BOOKING_STATUSES).map((status) => (
                    <Chip
                        key={status}
                        label={t(`bookings.statusValues.${status}`)}
                        onClick={() => selectStatus(status)}
                        variant={filters.statuses.includes(status) ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: filters.statuses.includes(status)
                                ? BOOKING_STATUS_COLORS[status]
                                : 'transparent',
                            color: filters.statuses.includes(status)
                                ? 'background.paper'
                                : undefined,
                            borderColor: BOOKING_STATUS_COLORS[status],
                        }}
                    />
                ))}
            </Grid2>

            <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ pr: 7 }}>
                    {t('timeslots.title')}
                    <IconButton
                        onClick={() => setPickerOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TimeslotCalendar
                        timeslots={timeslotsData?.timeslots ?? []}
                        selectedTimeslot={filters.timeslotId}
                        onTimeslotSelect={(timeslotId) => {
                            handleFilterChange('timeslotId', timeslotId);
                            setPickerOpen(false);
                        }}
                        slotTemplate={getSlotTemplate(showById.get(String(filters.showId)))}
                    />
                </DialogContent>
            </Dialog>

            <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
                <Box sx={{ width: 'min(350px, 100vw)', p: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                        }}
                    >
                        <Typography variant="h6">{t('bookings.advancedFiltering')}</Typography>
                        <IconButton onClick={toggleDrawer(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Grid2 container spacing={3}>
                        <Grid2 size={{ xs: 12 }}>
                            <TextField
                                label={t('bookings.searchById')}
                                variant="outlined"
                                type="number"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filters.bookingId || ''}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'bookingId',
                                        e.target.value === '' ? undefined : Number(e.target.value),
                                    )
                                }
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                            <TextField
                                label={t('bookings.searchByConfirmationCode')}
                                variant="outlined"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filters.confirmationCode || ''}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'confirmationCode',
                                        e.target.value === '' ? undefined : e.target.value,
                                    )
                                }
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel shrink>{t('bookings.status')}</InputLabel>
                                <Select
                                    value={filters.statuses[0] || ''}
                                    label={t('bookings.status')}
                                    onChange={(e) => selectStatus(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>{t('common.all')}</em>
                                    </MenuItem>
                                    {Object.values(BOOKING_STATUSES).map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {t(`bookings.statusValues.${status}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                        {canViewCommissionGroups && (
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>
                                        {t('bookings.filterLabels.commissionGroup')}
                                    </InputLabel>
                                    <Select
                                        value={filters.commissionGroupId ?? ''}
                                        label={t('bookings.filterLabels.commissionGroup')}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'commissionGroupId',
                                                e.target.value || null,
                                            )
                                        }
                                        MenuProps={{
                                            PaperProps: { sx: { maxHeight: 300 } },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>{t('common.all')}</em>
                                        </MenuItem>
                                        {visibleCommissionGroups.map((group) => (
                                            <MenuItem key={group.id} value={group.id}>
                                                {formatCommissionGroup(group)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid2>
                        )}
                        {canViewPaymentMethods && (
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>
                                        {t('bookings.filterLabels.paymentMethod')}
                                    </InputLabel>
                                    <Select
                                        value={filters.paymentMethodSlug ?? ''}
                                        label={t('bookings.filterLabels.paymentMethod')}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'paymentMethodSlug',
                                                e.target.value || null,
                                            )
                                        }
                                        MenuProps={{
                                            PaperProps: { sx: { maxHeight: 300 } },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>{t('common.all')}</em>
                                        </MenuItem>
                                        {visiblePaymentMethods.map((pm) => (
                                            <MenuItem key={pm.slug} value={pm.slug}>
                                                {pm.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid2>
                        )}
                    </Grid2>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={toggleDrawer(false)}
                        sx={{ mt: 3 }}
                    >
                        {t('common.close')}
                    </Button>
                </Box>
            </Drawer>
        </Grid2>
    );
};

export default BookingsFilter;
